import { useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, CircularProgress, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Stack, Typography, Divider, useMediaQuery, useTheme,
  Menu as MuiMenu, MenuItem, Chip, Tooltip,
} from '@mui/material';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Activity, Calendar, Pill, FlaskConical, FileText, Receipt,
  Package, User, LogOut, X, Search, ChevronLeft, ChevronRight, Building2, Check, MessageCircle,
  Heart, Users, ArrowLeftRight, ShieldCheck,
} from 'lucide-react';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import { usePatientTenant } from '../../contexts/PatientTenantContext';
import { usePatientDependents } from '../../contexts/PatientDependentContext';
import { getHealthcareColors } from '../../theme/clinicTheme';
import { PrimaryClinicBadge } from './PrimaryClinicBadge';
import { patientApi } from '../../lib/api';
import { extractData } from '../../hooks/useQueries';
import toast from 'react-hot-toast';

const SIDEBAR_WIDTH = 248;
const SIDEBAR_WIDTH_COLLAPSED = 76;
// Rail width below md — the sidebar is always visible now, so phones need a narrow form.
const SIDEBAR_WIDTH_RAIL = 60;
const SIDEBAR_COLLAPSED_KEY = 'patient_sidebar_collapsed';

interface NavItem { label: string; path: string; icon: typeof LayoutDashboard; badgeKey?: 'messages' }
interface NavGroup { title: string; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  { title: 'Overview', items: [
    { label: 'Dashboard', path: '/patient/dashboard', icon: LayoutDashboard },
    { label: 'Find Care', path: '/patient/find-care', icon: Search },
  ] },
  { title: 'Care', items: [
    { label: 'Appointments', path: '/patient/appointments', icon: Calendar },
    { label: 'Messages', path: '/patient/messages', icon: MessageCircle, badgeKey: 'messages' },
  ] },
  { title: 'Health Records', items: [
    { label: 'Vitals', path: '/patient/vitals', icon: Activity },
    { label: 'Prescriptions', path: '/patient/prescriptions', icon: Pill },
    { label: 'Lab Results', path: '/patient/lab-results', icon: FlaskConical },
    { label: 'My Records', path: '/patient/records', icon: FileText },
    { label: 'Referrals & Transfers', path: '/patient/referrals', icon: ArrowLeftRight },
    { label: 'Data Sharing', path: '/patient/consents', icon: ShieldCheck },
  ] },
  { title: 'Account', items: [
    { label: 'My Bills', path: '/patient/bills', icon: Receipt },
    { label: 'My Plans', path: '/patient/plans', icon: Package },
    { label: 'Family', path: '/patient/family', icon: Users },
    { label: 'Profile', path: '/patient/profile', icon: User },
  ] },
];

const UNREAD_POLL_MS = 25_000;

function initials(name?: string) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

/**
 * Persistent shell for the whole patient portal — mounted once by the pathless
 * <PatientPortalRoute> parent route so it survives navigation between pages.
 *
 * Nav is a grouped left sidebar (desktop) / drawer (mobile) rather than a single flat row —
 * flat top-nav stopped scaling once Messages/Find Care were added (11 items wrapping awkwardly).
 */
export function PatientPortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient, loading, signOut } = usePatientAuth();
  const { linkedClinics, activeClinic, isMultiClinic, switchClinic } = usePatientTenant();
  const { dependents, activeDependent, switchToDependent, switchToSelf } = usePatientDependents();
  const activeDependents = dependents.filter((d) => d.status === 'ACTIVE');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clinicMenuAnchor, setClinicMenuAnchor] = useState<null | HTMLElement>(null);
  const [familyMenuAnchor, setFamilyMenuAnchor] = useState<null | HTMLElement>(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');

  // Below md the sidebar is always the icon rail regardless of the saved preference — an expanded
  // sidebar would cover a phone screen. `collapsed` stays the user's desktop preference.
  const railMode = isMobile || collapsed;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };
  const colors = getHealthcareColors();
  const prevUnreadRef = useRef<number | null>(null);

  useEffect(() => {
    if (!patient) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await patientApi.messages.getEligibleDoctors();
        const doctors = extractData<Array<{ unreadCount: number; name: string }>>(res ?? {}) ?? [];
        const total = doctors.reduce((sum, d) => sum + (d.unreadCount || 0), 0);
        if (cancelled) return;
        const prev = prevUnreadRef.current;
        if (prev != null && total > prev && location.pathname !== '/patient/messages') {
          const newest = doctors.find((d) => d.unreadCount > 0);
          toast(newest ? `New message from Dr. ${newest.name}` : 'You have a new message', {
            icon: '💬',
            id: 'patient-new-message',
          });
        }
        prevUnreadRef.current = total;
        setUnreadMessages(total);
      } catch { /* silent — badge just stays stale until the next successful poll */ }
    };
    poll();
    const id = setInterval(poll, UNREAD_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, activeClinic?.clinicId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!patient) return <Navigate to="/patient/login" replace />;

  const goTo = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    if (path === '/patient/messages') setUnreadMessages(0);
  };

  const badgeCount = (key?: NavItem['badgeKey']) => (key === 'messages' ? unreadMessages : 0);

  /**
   * Everything the removed top bar carried: brand, active clinic, dependent switching, messages,
   * profile and sign-out. The application-wide top bar is gone, so this lives at the top and bottom
   * of the sidebar instead.
   *
   * The "viewing X's records" safety banner is deliberately NOT here — it stays a full-width sticky
   * strip below, where a parent cannot miss whose chart they are on.
   */
  const SidebarChrome = ({ mini }: { mini: boolean }) => (
    <>
      <Box sx={{ background: colors.gradients.primary, color: '#fff', px: mini ? 1 : 2, py: 1.75 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={mini ? 'center' : 'flex-start'}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 2, p: 0.75, display: 'flex' }}>
            <Heart size={17} />
          </Box>
          {!mini && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
                Patient Portal
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }} noWrap>
                {activeDependent ? `Viewing ${activeDependent.name}` : `Welcome, ${patient.name}`}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      {!mini && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          {isMultiClinic ? (
            <Box onClick={(e) => setClinicMenuAnchor(e.currentTarget)} sx={{ cursor: 'pointer' }}>
              <PrimaryClinicBadge
                clinicName={activeClinic?.clinicName ?? patient.clinicName}
                clinicLogoUrl={patient.clinicLogoUrl}
                variant="chip"
              />
            </Box>
          ) : (
            <PrimaryClinicBadge
              clinicName={patient.clinicName}
              clinicLogoUrl={patient.clinicLogoUrl}
              variant="chip"
            />
          )}
        </Box>
      )}
    </>
  );

  const SidebarFooter = ({ mini }: { mini: boolean }) => (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
      <Stack
        direction={mini ? 'column' : 'row'}
        spacing={mini ? 0.5 : 0.25}
        alignItems="center"
        justifyContent={mini ? 'center' : 'flex-start'}
      >
        <Tooltip title="Messages" placement="right">
          <IconButton size="small" onClick={() => goTo('/patient/messages')} sx={{ color: 'text.secondary' }}>
            <Box sx={{ position: 'relative', display: 'flex' }}>
              <MessageCircle size={18} />
              {unreadMessages > 0 && (
                <Box sx={{
                  position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%',
                  bgcolor: '#ef4444', border: '2px solid', borderColor: 'background.paper',
                }} />
              )}
            </Box>
          </IconButton>
        </Tooltip>

        {activeDependents.length > 0 && (
          <Tooltip title="Switch family member" placement="right">
            <IconButton size="small" onClick={(e) => setFamilyMenuAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
              <Users size={17} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={patient.name} placement="right">
          <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.main', fontSize: '0.65rem', fontWeight: 700 }}>
            {initials(patient.name)}
          </Avatar>
        </Tooltip>

        <Tooltip title="Sign out" placement="right">
          <IconButton size="small" onClick={signOut} sx={{ color: 'text.secondary' }}>
            <LogOut size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const SidebarNav = ({ inDrawer = false }: { inDrawer?: boolean }) => {
    // The mobile drawer always shows full labels — collapsing only applies to the persistent desktop rail.
    const mini = collapsed && !inDrawer;

    return (
      <Box sx={{ py: 1 }}>
        {NAV_GROUPS.map((group, gi) => (
          <Box key={group.title} sx={{ mb: 0.5 }}>
            {mini ? (
              gi > 0 && <Divider sx={{ mx: 1.5, my: 1 }} />
            ) : (
              <Typography
                variant="caption"
                sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'block', fontWeight: 700, letterSpacing: 0.6, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.68rem' }}
              >
                {group.title}
              </Typography>
            )}
            <List sx={{ py: 0 }}>
              {group.items.map(({ label, path, icon: Icon, badgeKey }) => {
                const active = location.pathname === path;
                const count = badgeCount(badgeKey);
                const button = (
                  <ListItemButton
                    key={path}
                    selected={active}
                    onClick={() => goTo(path)}
                    sx={{
                      borderRadius: 2, mx: 1.25, my: 0.25, py: 0.9,
                      justifyContent: mini ? 'center' : 'flex-start',
                      transition: 'background-color 0.15s ease',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main', color: '#fff',
                        '& .MuiListItemIcon-root': { color: '#fff' },
                        boxShadow: `0 2px 10px ${colors.shadowPrimary}`,
                      },
                      '&.Mui-selected:hover': { bgcolor: 'primary.dark' },
                      '&:hover': { bgcolor: active ? undefined : 'action.hover' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: mini ? 0 : 36, justifyContent: 'center', position: 'relative' }}>
                      <Icon size={18} />
                      {mini && count > 0 && (
                        <Box sx={{
                          position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%',
                          bgcolor: 'error.main', border: '2px solid', borderColor: 'background.paper',
                        }} />
                      )}
                    </ListItemIcon>
                    {!mini && (
                      <>
                        <ListItemText primary={label} primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: '0.88rem' }} />
                        {count > 0 && (
                          <Chip
                            label={count > 9 ? '9+' : count}
                            size="small"
                            sx={{
                              height: 19, minWidth: 19, fontSize: '0.66rem', fontWeight: 700,
                              bgcolor: active ? 'rgba(255,255,255,0.25)' : 'error.main',
                              color: '#fff', '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        )}
                      </>
                    )}
                  </ListItemButton>
                );
                return mini ? (
                  <Tooltip key={path} title={label} placement="right">
                    {button}
                  </Tooltip>
                ) : button;
              })}
            </List>
          </Box>
        ))}
        <Divider sx={{ my: 1.5, mx: 2 }} />
        {mini ? (
          <Tooltip title="Sign out" placement="right">
            <ListItemButton onClick={signOut} sx={{ borderRadius: 2, mx: 1.25, justifyContent: 'center' }}>
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}><LogOut size={18} /></ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton onClick={signOut} sx={{ borderRadius: 2, mx: 1.25 }}>
            <ListItemIcon sx={{ minWidth: 36 }}><LogOut size={18} /></ListItemIcon>
            <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 500 }} />
          </ListItemButton>
        )}
        {inDrawer && <Box sx={{ height: 8 }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Gradient header — brand, active clinic, avatar. Page nav lives in the sidebar below. */}

      {/* Clinic and family switchers — triggered from the sidebar chrome above. The menus
          moved out of the removed AppBar; only their triggers relocated, not their contents. */}
      <MuiMenu anchorEl={clinicMenuAnchor} open={!!clinicMenuAnchor} onClose={() => setClinicMenuAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
          YOUR CLINICS
        </Typography>
        {linkedClinics.map((c) => (
          <MenuItem
            key={c.clinicId}
            selected={c.clinicId === activeClinic?.clinicId}
            onClick={() => { setClinicMenuAnchor(null); switchClinic(c.clinicId); }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
              <Avatar src={c.clinicLogoUrl} sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                <Building2 size={14} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{c.clinicName}</Typography>
                {c.isPrimary && <Chip label="Primary" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
              </Box>
              {c.clinicId === activeClinic?.clinicId && <Check size={16} />}
            </Stack>
          </MenuItem>
        ))}
      </MuiMenu>

      <MuiMenu anchorEl={familyMenuAnchor} open={!!familyMenuAnchor} onClose={() => setFamilyMenuAnchor(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
          VIEWING RECORDS FOR
        </Typography>
        <MenuItem selected={!activeDependent} onClick={() => { setFamilyMenuAnchor(null); if (activeDependent) switchToSelf(); }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>{initials(patient.name)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{patient.name}</Typography>
              <Chip label="Myself" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
            </Box>
            {!activeDependent && <Check size={16} />}
          </Stack>
        </MenuItem>
        {activeDependents.map((d) => (
          <MenuItem
            key={d.dependentPatientId}
            selected={d.dependentPatientId === activeDependent?.dependentPatientId}
            onClick={() => { setFamilyMenuAnchor(null); switchToDependent(d.dependentPatientId); }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 260 }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>{initials(d.name)}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{d.name}</Typography>
                <Chip label={d.relationshipType} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
              </Box>
              {d.dependentPatientId === activeDependent?.dependentPatientId && <Check size={16} />}
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setFamilyMenuAnchor(null); navigate('/patient/family'); }}>
          <ArrowLeftRight size={14} style={{ marginRight: 8 }} /> Manage family
        </MenuItem>
      </MuiMenu>

      {activeDependent && (
        <Box sx={{
          bgcolor: 'warning.light', color: 'warning.contrastText', px: 2, py: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap',
          // top: 0 — there is no longer a 64px app bar above this banner to clear.
          position: 'sticky', top: 0, zIndex: (t) => t.zIndex.drawer,
        }}>
          <Users size={14} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            You are viewing {activeDependent.name}'s records ({activeDependent.relationshipType.toLowerCase()})
          </Typography>
          <Box
            component="button"
            onClick={switchToSelf}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline',
              fontWeight: 700, fontSize: 'inherit', color: 'inherit', px: 0.5,
            }}
          >
            Switch back to myself
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Persistent sidebar (desktop) — width animates between full and icon-only (collapsed) */}
        <Box
          component="nav"
          sx={{
            // Visible at every breakpoint: the top bar is gone application-wide, so the rail is the
            // only route to navigation. Below md it is always the narrow icon form — an expanded
            // sidebar would cover a phone screen — and the menu button opens the Drawer instead.
            width: { xs: SIDEBAR_WIDTH_RAIL, md: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH },
            flexShrink: 0, display: 'flex', flexDirection: 'column',
            position: 'sticky', top: 0, height: '100vh',
            borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
            transition: 'width 0.2s ease',
          }}
        >
          <SidebarChrome mini={railMode} />
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            <SidebarNav />
          </Box>
          <SidebarFooter mini={railMode} />

          {/* Collapse/expand toggle — always pinned to the bottom of the rail */}
          <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton
                onClick={toggleCollapsed}
                size="small"
                sx={{
                  width: '100%', borderRadius: 2, justifyContent: collapsed ? 'center' : 'flex-end',
                  color: 'text.secondary',
                }}
              >
                {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Mobile drawer */}
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 272 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700 }}>
                  {initials(patient.name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>{patient.name}</Typography>
                  {patient.clinicName && (
                    <Typography variant="caption" color="text.secondary">{patient.clinicName}</Typography>
                  )}
                </Box>
              </Stack>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}><X size={18} /></IconButton>
            </Stack>
            <Divider />
            {isMultiClinic && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">YOUR CLINICS</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {linkedClinics.map((c) => (
                    <Box
                      key={c.clinicId}
                      onClick={() => switchClinic(c.clinicId)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                        bgcolor: c.clinicId === activeClinic?.clinicId ? 'primary.50' : 'transparent',
                      }}
                    >
                      <Avatar src={c.clinicLogoUrl} sx={{ width: 24, height: 24 }}><Building2 size={12} /></Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>{c.clinicName}</Typography>
                      {c.clinicId === activeClinic?.clinicId && <Check size={14} />}
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
            )}
            {activeDependents.length > 0 && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">VIEWING RECORDS FOR</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  <Box
                    onClick={() => activeDependent && switchToSelf()}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                      bgcolor: !activeDependent ? 'primary.50' : 'transparent',
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>{initials(patient.name)}</Avatar>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>{patient.name} (Myself)</Typography>
                    {!activeDependent && <Check size={14} />}
                  </Box>
                  {activeDependents.map((d) => (
                    <Box
                      key={d.dependentPatientId}
                      onClick={() => switchToDependent(d.dependentPatientId)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, cursor: 'pointer',
                        bgcolor: d.dependentPatientId === activeDependent?.dependentPatientId ? 'primary.50' : 'transparent',
                      }}
                    >
                      <Avatar sx={{ width: 24, height: 24 }}>{initials(d.name)}</Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>{d.name}</Typography>
                      {d.dependentPatientId === activeDependent?.dependentPatientId && <Check size={14} />}
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
            )}
            <SidebarNav inDrawer />
          </Box>
        </Drawer>

        <Box component="main" sx={{ flex: 1, minWidth: 0, maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
